import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';

export type Theme = 'dark' | 'light' | 'system';
export type ViewMode = 'grid' | 'list';

interface Settings {
  // Weergave
  theme: Theme;
  compactMode: boolean;
  defaultViewMode: ViewMode;

  // Afspelen
  defaultSpeed: number;
  autoplayNext: boolean;
  rememberPosition: boolean;
  defaultABRepeat: boolean;

  // Notificaties
  emailOnNewMessages: boolean;
  emailOnNewSongs: boolean;

  // Admin settings
  defaultVoiceParts: string;
  autoArchiveDays: number;
}

interface SettingsContextType {
  settings: Settings;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  resetSettings: () => void;
}

const defaultSettings: Settings = {
  theme: 'dark',
  compactMode: false,
  defaultViewMode: 'grid',
  defaultSpeed: 1.0,
  autoplayNext: true,
  rememberPosition: true,
  defaultABRepeat: false,
  emailOnNewMessages: true,
  emailOnNewSongs: true,
  defaultVoiceParts: 'Sopraan, Alt, Tenor, Bas',
  autoArchiveDays: 30,
};

const SettingsContext = createContext<SettingsContextType | null>(null);

export function SettingsProvider({ children }: { children: ReactNode }) {
  const [settings, setSettings] = useState<Settings>(() => {
    const saved = localStorage.getItem('leden-settings');
    if (saved) {
      try {
        return { ...defaultSettings, ...JSON.parse(saved) };
      } catch {
        return defaultSettings;
      }
    }
    return defaultSettings;
  });

  useEffect(() => {
    localStorage.setItem('leden-settings', JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    // Apply theme using data-theme attribute (matches CSS selectors in themes.css)
    const root = document.documentElement;
    if (settings.theme === 'system') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.setAttribute('data-theme', prefersDark ? 'dark' : 'light');
    } else {
      root.setAttribute('data-theme', settings.theme);
    }
  }, [settings.theme]);

  const updateSetting = <K extends keyof Settings>(key: K, value: Settings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const resetSettings = () => {
    setSettings(defaultSettings);
  };

  return (
    <SettingsContext.Provider value={{ settings, updateSetting, resetSettings }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
}
